import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  Subscription,
  throwError,
} from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { IPatient } from '../interfaces/IPatient';
import { ISearchPatient } from '../interfaces/ISearchPatient';
import { IPatientDetailed } from '../interfaces/Detailed/IPatientDetailed';
import { IDefinition, IDefinitions } from '../interfaces/IDefinitions';
import { saveAs } from 'file-saver';
import { IAdmissionFile } from '../interfaces/AdmissionPatient/AdmissionProfile/IAdmissionFile';
import { IAddDocument } from '../interfaces/AdmissionPatient/AdmissionProfile/IAddDocument';
import {
  IExistingPatient,
  IExistingPatientResponse,
} from '../interfaces/IExistingPatient';
import { TranslateService } from '@ngx-translate/core';
import { IExternalCymaDoctorDropdown } from '../interfaces/ExternalDoctors/IExternalCymaDoctorDropdown';
import { SwalToastService } from './swal.service';
import { IAdmissionReasonSummary } from '../interfaces/AdmissionPatient/IAdmissionReasonSummary';
import { AllergyService } from './allergy.service';
import { CarePlanService } from './carePlan.service';
import { CurrentMedicationService } from './currentMedication.service';
import { CurrentProblemsService } from './currentProblems.service';
import { DepartmentalQuestionnairesService } from './departmentalQuestionnaires.service';
import { HysteroscopyFileService } from './hyseroscopyFile.service';
import { ImagingService } from './imaging.service';
import { InitialAssessmentService } from './initialAssessment.service';
import { LaboratoryFileService } from './laboratoryFile.service';
import { MedicalAlertService } from './medicalAlert.service';
import { MedicalDeviceService } from './medicalDevice.service';
import { MedicalHistoryService } from './medicalHistory.service';
import { PastMedicationService } from './pastMedication.service';
import { PregnancyHistoryService } from './pregnancyHistory.service';
import { PregnancyOutcomeService } from './pregnancyOutcome.service';
import { PregnancyStatusService } from './pregnancyStatus.service';
import { ProcedureService } from './procedure.service';
import { ResolvedProblemsService } from './resolvedProblems.service';
import { SelfReportService } from './selfReport.service';
import { SocialHistoryService } from './socialHistory.service';
import { SupportingDocumentsService } from './supportingDocuments.service';
import { TravelHistoryService } from './travelHistory.service';
import { VaccinationService } from './vaccination.service';
import { VitalSignService } from './vitalSign.service';

@Injectable({ providedIn: 'root' })
export class PatientService {
  public patientInfoSub = new BehaviorSubject<IPatientDetailed>({});

  public patientLoadingSub = new BehaviorSubject<boolean>(false);

  public showPatientAdmissionReasonSummarySub = new BehaviorSubject<boolean>(
    false
  );
  public patientAdmissionReasonSummarySub = new BehaviorSubject<
    IAdmissionReasonSummary | undefined
  >(undefined);

  public patientIDsignal = signal('');

  public definitionsSignal = signal([] as IDefinition[]);

  public documentsSub = new BehaviorSubject<IAdmissionFile[]>([]);

  constructor(
    private _http: HttpClient,
    private translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly router: Router,
    private readonly injector: Injector
  ) {}

  // RESET STALE PER-PATIENT CLINICAL DATA BEFORE ACCESSING A DIFFERENT PATIENT.
  // These services all inject PatientService themselves, so they are resolved
  // lazily via the Injector here instead of the constructor to avoid a
  // circular DI dependency (PatientService <-> AllergyService, etc.).
  resetClinicalData(): void {
    this.injector.get(AllergyService).allergyDataSub.next({});
    this.injector.get(VaccinationService).vaccinationDataSub.next([]);
    this.injector.get(MedicalAlertService).medicalAlertDataSub.next([]);
    this.injector.get(MedicalHistoryService).medicalHistoryDataSub.next([]);
    this.injector.get(ProcedureService).procedureDataSub.next({});
    this.injector.get(MedicalDeviceService).medicalDeviceDataSub.next({});
    this.injector.get(CurrentProblemsService).currentProblemDataSub.next({});
    this.injector
      .get(ResolvedProblemsService)
      .resolvedProblemDataSub.next([]);
    this.injector
      .get(PregnancyHistoryService)
      .pregnancyHistoryDataSub.next([]);
    this.injector
      .get(PregnancyOutcomeService)
      .pregnancyOutcomeDataSub.next([]);
    this.injector.get(PregnancyStatusService).pregnancyStatusDataSub.next([]);
    this.injector.get(CarePlanService).carePlanDataSub.next([]);
    this.injector.get(SocialHistoryService).socialHistoryDataSub.next([]);
    this.injector
      .get(InitialAssessmentService)
      .initialAssessmentDataSub.next([]);
    this.injector
      .get(DepartmentalQuestionnairesService)
      .departmentalQuestionnairesDataSub.next([]);
    this.injector.get(TravelHistoryService).travelHistoryDataSub.next([]);
    this.injector
      .get(CurrentMedicationService)
      .currentMedicationDataSub.next({});
    this.injector.get(PastMedicationService).pastMedicationDataSub.next([]);
    this.injector.get(ImagingService).imagingDataSub.next([]);
    this.injector.get(LaboratoryFileService).laboratoryDataSub.next([]);
    this.injector
      .get(SupportingDocumentsService)
      .supportingDocumentDataSub.next([]);
    this.injector.get(HysteroscopyFileService).hysteroscopyDataSub.next([]);
    this.injector.get(VitalSignService).vitalSignDataSub.next([]);
    this.injector.get(SelfReportService).selfReportDataSub.next([]);
  }

  getAllPatients(): Observable<IGenericServiceResponse<IPatientDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IPatientDetailed[]>>(
      `${environment.BACKEND_URL}Patient`,
      {
        headers: headers,
      }
    );
  }

  getPatients(
    paramsObj: ISearchPatient,
    pageSize: number = 10,
    pageNumber: number = 1
  ): Observable<IGenericServiceResponse<IPatient[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IPatient[]>>(
      `${environment.BACKEND_URL}Patient/Search/${pageNumber}/${pageSize}`,
      {
        headers: headers,
        params: new HttpParams({ fromObject: paramsObj as any }),
      }
    );
  }
  getPatientsGOC(
    paramsObj: ISearchPatient,
    pageSize: number = 10,
    pageNumber: number = 1
  ): Observable<IGenericServiceResponse<IPatient[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IPatient[]>>(
      `${environment.BACKEND_URL}Patient/SearchPilotStudy/${pageNumber}/${pageSize}`,
      {
        headers: headers,
        params: new HttpParams({ fromObject: paramsObj as any }),
      }
    );
  }

  getPatientByID(
    id: string
  ): Observable<IGenericServiceResponse<IPatientDetailed>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http
      .get<IGenericServiceResponse<IPatientDetailed>>(
        `${environment.BACKEND_URL}Patient/${id}`,
        {
          headers: headers,
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 403) {
            this.router.navigate(['/ehr/not-found']);
          }
          return throwError(() => err);
        })
      );
  }

  getDefinitions(): Observable<IGenericServiceResponse<IDefinitions>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IDefinitions>>(
      `${environment.BACKEND_URL}Definitions`,
      {
        headers: headers,
      }
    );
  }

  async getFiles(patientId: string) {
    this.getPatientFiles(patientId).subscribe({
      next: (response) => {
        this.documentsSub.next(response.data);
      },
      error: (err) => {
        this.swalToastService.toastError('Unable to retrieve documents');
        console.error('Error fetching Documents:', err);
      },
    });
  }

  getPatientFiles(
    patientId: string
  ): Observable<IGenericServiceResponse<IAdmissionFile[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.get<IGenericServiceResponse<IAdmissionFile[]>>(
      `${environment.BACKEND_URL}Patient/Files/${patientId}`,
      {
        headers: headers,
      }
    );
  }

  addDocument(documentObj: IAddDocument): Observable<any> {
    const formData = new FormData();

    // TO BE CHANGED
    if (documentObj?.files) {
      documentObj.files.forEach((file, index) => {
        formData.append(`Files`, file);
      });
    }

    formData.append('FileTypeId', documentObj?.fileTypeId!);
    formData.append('PatientId', documentObj?.patientId!);

    return this._http.post(
      `${environment.BACKEND_URL}Patient/UploadPatientFile`,
      formData
    );
  }

  getProfilePicture(id: string): Observable<Blob> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http
      .get(`${environment.BACKEND_URL}Patient/DownloadProfilePicture/${id}`, {
        headers: headers,
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          console.error('Error downloading profile picture:', error);
          throw error; // rethrow error to propagate it to the subscriber
        })
      );
  }

  removeProfilePicture(patientId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Patient/DeleteProfilePicture/${patientId}`,
      { headers }
    );
  }

  downloadDocument(fileName: string, fileId: string, patientId: string): void {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this._http
      .get(
        `${environment.BACKEND_URL}Patient/DownloadPatientFile/${fileId}/${patientId}`,
        { headers, responseType: 'blob', observe: 'response' }
      )
      .subscribe({
        next: (res: HttpResponse<Blob>) => {
          const blob = res.body!;
          const contentType =
            res.headers.get('Content-Type') ?? 'application/octet-stream';
          const typedBlob = new Blob([blob], { type: contentType });
          // Just save — do NOT open a tab here
          saveAs(typedBlob, fileName);
        },
        error: (err) => {
          console.error('Error downloading file:', err);
        },
      });
  }

  /** VIEW ONLY: open PDF/image in a NEW TAB (no download) */
  /** VIEW ONLY: open in new tab using the *download* endpoint */
  viewDocumentOpenTab(fileId: string, patientId: string): void {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this._http
      .get(
        `${environment.BACKEND_URL}Patient/DownloadPatientFile/${fileId}/${patientId}`,
        { headers, responseType: 'blob', observe: 'response' }
      )
      .subscribe({
        next: (res: HttpResponse<Blob>) => {
          const contentType =
            res.headers.get('Content-Type') ?? 'application/octet-stream';
          const url = window.URL.createObjectURL(
            new Blob([res.body!], { type: contentType })
          );
          window.open(url, '_blank', 'noopener');
          // optionally revoke later:
          // setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
        },
        error: (err) => console.error('Error opening file in new tab:', err),
      });
  }

  deleteDocument(
    fileId: string,
    patientId: string
  ): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Patient/DeletePatientFile/${fileId}/${patientId}`,
      {
        headers: headers,
      }
    );
  }

  exportAdmissionData(paramsObj: ISearchPatient): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this._http
      .get(
        `${environment.BACKEND_URL}PatientRegistration/ExportAdmissionData`,
        {
          headers: headers,
          responseType: 'blob',
          params: new HttpParams({ fromObject: paramsObj as any }),
        }
      )
      .subscribe(
        (blob) => {
          const file = new Blob([blob], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(file, 'Patients_' + this.formatDateToCustom() + '.xlsx');
        },
        (error) => {
          this.swalToastService.toastError('Unable to download the document');
          console.error('Error downloading PDF:', error);
        }
      );
  }

  formatDateToCustom(): string {
    const date = new Date();
    return `${date.getFullYear()}${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}${date
      .getHours()
      .toString()
      .padStart(2, '0')}${date.getMinutes().toString().padStart(2, '0')}${date
      .getSeconds()
      .toString()
      .padStart(2, '0')}`;
  }

  updateAdmissionPatient(patientId: number, patientObj: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}Patient/${patientId}`,
      patientObj,
      {
        headers: headers,
      }
    );
  }

  patientExists(
    existingPatient: IExistingPatient
  ): Observable<IGenericServiceResponse<IExistingPatientResponse>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post<IGenericServiceResponse<IExistingPatientResponse>>(
      `${environment.BACKEND_URL}Patient/Exists`,
      existingPatient,
      {
        headers: headers,
      }
    );
  }

  getExternalCymaDoctors(): Observable<IExternalCymaDoctorDropdown[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http
      .get<{ data: { externalCymaDoctors: IExternalCymaDoctorDropdown[] } }>(
        `${environment.BACKEND_URL}Patient/ExternalDoctors`,
        { headers }
      )
      .pipe(
        map((response) => response?.data?.externalCymaDoctors || []) // Safely extract the array
      );
  }

  getAdmissionReasonSummary(patientId: string): Subscription {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this._http
      .get<IGenericServiceResponse<IAdmissionReasonSummary> | null>(
        `${environment.BACKEND_URL}Patient/AdmissionReasonSummary/${patientId}`,
        { headers }
      )
      .subscribe({
        next: (response) => {
          // Handle 204/no body safely:
          if (!response || !response.data) {
            this.patientAdmissionReasonSummarySub.next(undefined); // hide button
            return;
          }
          this.patientAdmissionReasonSummarySub.next(response.data);
        },
        error: (_err) => {
          // Network/4xx/5xx: also hide the button and swallow the error
          this.patientAdmissionReasonSummarySub.next(undefined);
        },
      });
  }
}
